import { PaymentCycle } from "../../models/enumerations/payment-cycle";
import { ServiceBase } from "./service-base";

export interface CreateCheckoutSetupIntentRequest {
    email: string;
    name: string;
    preferredLocale?: string;
}

export interface CreateCheckoutSetupIntentResponse {
    customerId: string;
    clientSecret: string;
}

export interface CompleteCheckoutCreateRequest {
    customerId: string;
    paymentMethodId: string;
    selectedPriceId: string;
    userAddOnPriceId: string;
    licenseAddOnPriceId: string;
    couponId?: string;
}

export interface CompleteCheckoutCreateResponse {
    planSubscriptionId: string;
    addOnSubscriptionId: string;
    currentPeriodEnd: number;
    planAmount: number;
}

export interface ValidatePromotionCodeRequest {
    code: string;
    customerId?: string;
    productId: string;
    paymentCycle: PaymentCycle;
    totalPrice: number;
}

export interface ValidatePromotionCodeResponse {
    valid: boolean;
    message?: string;
    code?: string;
    couponId?: string;
    discountAmount?: number;
    discountLabel?: string;
    applyToNextPayment?: boolean;
}

export interface ResolvePromotionCodeRequest {
    couponId: string;
    productId: string;
    paymentCycle: PaymentCycle;
    totalPrice: number;
}

export interface ResolvePromotionCodeResponse {
    valid: boolean;
    message?: string;
    couponId?: string;
    discountAmount?: number;
    discountLabel?: string;
    applyToNextPayment?: boolean;
}

export interface GetBillingSummaryRequest {
    email: string;
    stripePlanSubscription?: string;
    stripeAddOnSubscription?: string;
}

export interface GetBillingSummaryResponse {
    isSubscriptionCancelled: boolean;
    stripeCustomerFailedPayment: boolean;
    paymentMethod: {
        brand: string | null;
        lastFour: string | null;
        expiration: string | null;
    };
}

export interface GetBillingInvoicesRequest {
    email: string;
    stripePlanSubscription?: string;
    stripeAddOnSubscription?: string;
}

export interface BillingInvoice {
    productId: string | null;
    effectiveAt: number | null;
    amountPaid: number;
    hostedInvoiceUrl: string | null;
}

export interface GetBillingInvoicesResponse {
    invoices: BillingInvoice[];
}

export interface CreatePaymentMethodSetupIntentRequest {
    email: string;
}

export interface CreatePaymentMethodSetupIntentResponse {
    customerId: string;
    clientSecret: string;
}

export interface CompletePaymentMethodSetupRequest {
    customerId: string;
    paymentMethodId: string;
}

export interface CompletePaymentMethodSetupResponse {
    success: boolean;
}

export interface CancelSubscriptionRequest {
    subscriptionId: string;
    email: string;
    planType?: string;
    paymentCycle?: PaymentCycle;
    renewDate?: string;
    isUserSubscription?: boolean;
}

export interface CancelSubscriptionResponse {
    success: boolean;
}

export interface ReactivateSubscriptionRequest {
    subscriptionId: string;
}

export interface ReactivateSubscriptionResponse {
    success: boolean;
}

export interface CreateBillingPortalSessionRequest {
    email: string;
    thirdPartyProductId?: string;
    mySymmioMonthlyPriceId?: string;
    mySymmioYearlyPriceId?: string;
    preferredLanguage?: string;
    allowSubscriptionChange?: boolean;
    returnUrl?: string;
}

export interface CreateBillingPortalSessionResponse {
    url: string;
}

export interface CreateUpdateSetupIntentRequest {
    email: string;
}

export interface CreateUpdateSetupIntentResponse {
    customerId: string;
    clientSecret: string;
}

export interface CompleteCheckoutUpdateRequest {
    customerId: string;
    paymentMethodId: string;
    currentSubscriptionId: string;
    newPriceId: string;
    couponId?: string;
}

export interface CompleteCheckoutUpdateResponse {
    currentPeriodEnd: number;
    planAmount: number;
}

export interface GetProrationPreviewRequest {
    customerId?: string;
    currentSubscriptionId: string;
    newPriceId: string;
    couponId?: string;
}

export interface GetProrationPreviewResponse {
    amountDue: number;
    proratedSum?: number;
    discountAmount?: number;
}

export interface UpdateCustomerEmailRequest {
    currentEmail: string;
    newEmail: string;
}

export interface UpdateCustomerEmailResponse {
    success: boolean;
}

export interface CheckAndUpdateHiddenLimitRequest {
    stripeAddOnSubscriptionId: string;
    userAddOnPriceId: string;
    licenseAddOnPriceId: string;
    currentLicenseCount: number;
}

export interface CheckAndUpdateHiddenLimitResponse {
    success: boolean;
}

export interface GetSubscriptionDetailsRequest {
    subscriptionId: string;
}

export interface SubscriptionItemDetails {
    priceId: string;
    productId: string | null;
    quantity: number;
    amount: number;
    interval: string | null;
    intervalCount: number;
}

export interface GetSubscriptionDetailsResponse {
    cancelAtPeriodEnd: boolean;
    couponId: string | null;
    planInterval: string | null;
    planAmount: number;
    currentPeriodStart: number;
    currentPeriodEnd: number;
    items: SubscriptionItemDetails[];
}

export interface ApplyCouponToSubscriptionRequest {
    subscriptionId: string;
    couponId: string;
}

export interface ApplyCouponToSubscriptionResponse {
    success: boolean;
}

export interface CreateAddonPlaceholderSubscriptionRequest {
    email: string;
    userAddOnPriceId: string;
    licenseAddOnPriceId: string;
}

export interface CreateAddonPlaceholderSubscriptionResponse {
    subscriptionId: string;
}

export interface CompleteCheckoutReactivateRequest {
    customerId: string;
    paymentMethodId: string;
    isReactivatingPlan: boolean;
    isReactivatingAddOn: boolean;
    planPriceId?: string;
    addOnItems?: Array<{ price: string; quantity: number }>;
    userAddOnPriceId?: string;
    licenseAddOnPriceId?: string;
}

export interface CompleteCheckoutReactivateResponse {
    planSubscriptionId?: string;
    addOnSubscriptionId?: string;
    currentPeriodEnd?: number;
    planInterval?: string;
    planItems?: SubscriptionItemDetails[];
    addOnItems?: SubscriptionItemDetails[];
}

export interface AddOnItemDetails {
    itemId: string;
    priceId: string;
    productId: string | null;
    quantity: number;
    amount: number;
    interval: string | null;
    intervalCount: number;
}

export interface AddOnSchedulePhase {
    startDate?: number;
    endDate?: number;
    iterations?: number;
    items: Array<{ price: string; quantity: number }>;
}

export interface AddOnScheduleDetails {
    id: string;
    currentPhase: any;
    phases: AddOnSchedulePhase[];
}

export interface GetAddOnDetailsRequest {
    stripeAddOnSubscriptionId: string;
}

export interface GetAddOnDetailsResponse {
    subscriptionId: string;
    currentPeriodEnd: number;
    scheduleId: string | null;
    items: AddOnItemDetails[];
    schedule: AddOnScheduleDetails | null;
}

export interface PurchaseAddOnRequest {
    subscriptionId: string;
    subscriptionItemId: string;
    priceId: string;
    quantity: number;
    prorationDate: number;
}

export interface PurchaseAddOnResponse {
    subscriptionId: string;
    currentPeriodEnd: number;
    scheduleId: string | null;
    schedule: AddOnScheduleDetails | null;
}

export interface ScheduleAddOnRemovalRequest {
    subscriptionId: string;
    priceId: string;
    quantityToRemove: number;
}

export interface ScheduleAddOnRemovalResponse {
    scheduleId: string;
    billingCycleEnd: number;
    phases: AddOnSchedulePhase[];
}

export interface CalculateProrationRequest {
    subscriptionId: string;
    subscriptionItemId: string;
    priceId: string;
    newQuantity: number;
    totalQuantity: number;
    prorationDate: number;
}

export interface CalculateProrationResponse {
    proratedRate: number;
    refundedAmount: number;
    fullRate: number;
}

export interface TrialSignupCustomerRequest {
    email: string;
    firstName: string;
    lastName: string;
    preferredLocale?: string;
}

export interface TrialSignupCustomerResponse {
    customerId: string;
}

export interface CompleteUserSubscriptionRequest {
    customerId: string;
    paymentMethodId: string;
    priceId: string;
}

export interface CompleteUserSubscriptionResponse {
    subscriptionId: string;
    currentPeriodEnd: number;
}

export interface CreateThirdPartyProductRequest {
    name: string;
    monthlyPrice: number;
    yearlyPrice: number;
}

export interface CreateThirdPartyProductResponse {
    productId: string;
    monthlyPriceId: string;
    yearlyPriceId: string;
}

const BillingService = {
    createCheckoutSetupIntent:
        ServiceBase.post<CreateCheckoutSetupIntentResponse, CreateCheckoutSetupIntentRequest>(
            "/billing/checkout/create/setup-intent"
        ),
    completeCheckoutCreate:
        ServiceBase.post<CompleteCheckoutCreateResponse, CompleteCheckoutCreateRequest>(
            "/billing/checkout/create/complete"
        ),
    validatePromotionCode:
        ServiceBase.post<ValidatePromotionCodeResponse, ValidatePromotionCodeRequest>(
            "/billing/promotion-codes/validate"
        ),
    resolvePromotionCode:
        ServiceBase.post<ResolvePromotionCodeResponse, ResolvePromotionCodeRequest>(
            "/billing/promotion-codes/resolve"
        ),
    getBillingSummary:
        ServiceBase.post<GetBillingSummaryResponse, GetBillingSummaryRequest>(
            "/billing/summary"
        ),
    getBillingInvoices:
        ServiceBase.post<GetBillingInvoicesResponse, GetBillingInvoicesRequest>(
            "/billing/invoices"
        ),
    createPaymentMethodSetupIntent:
        ServiceBase.post<CreatePaymentMethodSetupIntentResponse, CreatePaymentMethodSetupIntentRequest>(
            "/billing/payment-method/setup-intent"
        ),
    completePaymentMethodSetup:
        ServiceBase.post<CompletePaymentMethodSetupResponse, CompletePaymentMethodSetupRequest>(
            "/billing/payment-method/complete"
        ),
    cancelSubscription:
        ServiceBase.post<CancelSubscriptionResponse, CancelSubscriptionRequest>(
            "/billing/subscription/cancel"
        ),
    reactivateSubscription:
        ServiceBase.post<ReactivateSubscriptionResponse, ReactivateSubscriptionRequest>(
            "/billing/subscription/reactivate"
        ),
    createBillingPortalSession:
        ServiceBase.post<CreateBillingPortalSessionResponse, CreateBillingPortalSessionRequest>(
            "/billing/portal/session"
        ),
    createUpdateSetupIntent:
        ServiceBase.post<CreateUpdateSetupIntentResponse, CreateUpdateSetupIntentRequest>(
            "/billing/checkout/update/setup-intent"
        ),
    completeCheckoutUpdate:
        ServiceBase.post<CompleteCheckoutUpdateResponse, CompleteCheckoutUpdateRequest>(
            "/billing/checkout/update/complete"
        ),
    getProrationPreview:
        ServiceBase.post<GetProrationPreviewResponse, GetProrationPreviewRequest>(
            "/billing/checkout/update/proration-preview"
        ),
    updateCustomerEmail:
        ServiceBase.post<UpdateCustomerEmailResponse, UpdateCustomerEmailRequest>(
            "/billing/customer/update-email"
        ),
    checkAndUpdateHiddenLimit:
        ServiceBase.post<CheckAndUpdateHiddenLimitResponse, CheckAndUpdateHiddenLimitRequest>(
            "/billing/subscription-schedule/check-and-update"
        ),
    getSubscriptionDetails:
        ServiceBase.post<GetSubscriptionDetailsResponse, GetSubscriptionDetailsRequest>(
            "/billing/subscription/details"
        ),
    applyCouponToSubscription:
        ServiceBase.post<ApplyCouponToSubscriptionResponse, ApplyCouponToSubscriptionRequest>(
            "/billing/subscription/apply-coupon"
        ),
    createAddonPlaceholderSubscription:
        ServiceBase.post<CreateAddonPlaceholderSubscriptionResponse, CreateAddonPlaceholderSubscriptionRequest>(
            "/billing/subscription/create-addon-placeholder"
        ),
    completeCheckoutReactivate:
        ServiceBase.post<CompleteCheckoutReactivateResponse, CompleteCheckoutReactivateRequest>(
            "/billing/checkout/reactivate/complete"
        ),
    getAddOnDetails:
        ServiceBase.post<GetAddOnDetailsResponse, GetAddOnDetailsRequest>(
            "/billing/add-ons/details"
        ),
    purchaseAddOn:
        ServiceBase.post<PurchaseAddOnResponse, PurchaseAddOnRequest>(
            "/billing/add-ons/purchase"
        ),
    scheduleAddOnRemoval:
        ServiceBase.post<ScheduleAddOnRemovalResponse, ScheduleAddOnRemovalRequest>(
            "/billing/add-ons/schedule-removal"
        ),
    calculateProration:
        ServiceBase.post<CalculateProrationResponse, CalculateProrationRequest>(
            "/billing/calculate-proration"
        ),
    trialSignupCustomer:
        ServiceBase.post<TrialSignupCustomerResponse, TrialSignupCustomerRequest>(
            "/billing/trial-signup/customer"
        ),
    completeUserSubscription:
        ServiceBase.post<CompleteUserSubscriptionResponse, CompleteUserSubscriptionRequest>(
            "/billing/checkout/user-subscription/complete"
        ),
    createThirdPartyProductAndPrices:
        ServiceBase.post<CreateThirdPartyProductResponse, CreateThirdPartyProductRequest>(
            "/billing/third-party/create-product-and-prices"
        ),
};

export default BillingService;